import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  isDateInputComplete,
  maskDateInput,
  parseDateInput,
} from "@/lib/date-input";
import { cn } from "@/lib/utils";

interface DateInputProps {
  /** Ko'rinadigan qiymat — `DD.MM.YYYY` yoki `DD.MM.YYYY hh:mm`. */
  value: string;
  /** To'liq va HAQIQIY sana yozilganda (yoki tozalanganda) chaqiriladi. */
  onCommit: (value: string) => void;
  /** Vaqt qismi. Standart holatda faqat sana. */
  withTime?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Sana kiritish maydoni.
 *
 * Tanlagich (calendar picker) EMAS, yozish uchun: admin qidirayotgan
 * sanani biladi va uni yozib bo'ladigan payt tanlagichda uch marta
 * bosish kerak bo'lardi. Ajratuvchilarni maydon o'zi qo'yadi, ya'ni
 * `03092026` ham `03.09.2026` ga aylanadi.
 *
 * Qidiruv HAR bosishda emas: qolip to'lganda yoki `Enter` bosilganda.
 * Yarim yozilgan sana ("03.09.20") bilan so'rov yuborish natijani
 * behuda tozalab, ro'yxatni "topilmadi" holatiga tushirardi.
 *
 * Vaqt qismi yozilmasa ham qidiruv ishlaydi — u holda butun kun
 * qidiriladi (`dateInputRange`).
 */
export function DateInput({
  value,
  onCommit,
  withTime = false,
  autoFocus = false,
  className,
}: Readonly<DateInputProps>) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const [previous, setPrevious] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Tashqi qiymat o'zgarsa (masalan filtr tozalansa) ichkarisi ham
  // yangilanadi. Render paytidagi `setState` — React renderni darhol
  // yangi holat bilan qayta boshlaydi, qo'shimcha render bo'lmaydi.
  if (previous !== value) {
    setPrevious(value);
    setDraft(value);
  }

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Yozib bo'lingan, lekin mavjud bo'lmagan sana ("32.09.2026") — xato,
  // va u jimgina "natija yo'q" ga aylanmasligi kerak.
  const invalid =
    draft.length > 0 && isDateInputComplete(draft, withTime) && !parseDateInput(draft);

  const commit = (next: string) => {
    if (next === "" || parseDateInput(next)) onCommit(next);
  };

  const handleChange = (raw: string) => {
    const next = maskDateInput(raw, withTime);
    setDraft(next);

    // Vaqtli maydonda sana qismining o'zi ham to'liq javob: qolip
    // to'lishini kutish "shu kun bo'yicha qidirish" ni imkonsiz
    // qilardi. Vaqt yozilsa, qidiruv o'sha daqiqaga toraytiriladi.
    const complete = isDateInputComplete(next) || (withTime && isDateInputComplete(next, true));

    if (next === "" || complete) commit(next);
  };

  const clear = () => {
    setDraft("");
    onCommit("");
    inputRef.current?.focus();
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        ref={inputRef}
        inputMode="numeric"
        // `placeholder` ayni paytda qolipning o'zi: qaysi tartibda
        // yozilishi (kun-oy-yil) mamlakatga qarab farq qiladi.
        placeholder={withTime ? DATE_TIME_FORMAT : DATE_FORMAT}
        aria-label={withTime ? DATE_TIME_FORMAT : DATE_FORMAT}
        aria-invalid={invalid}
        value={draft}
        onChange={event => handleChange(event.target.value)}
        onKeyDown={event => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          commit(draft);
        }}
        onBlur={() => commit(draft)}
        className="w-64 border-none tabular-nums shadow-none focus-visible:ring-[1px]"
      />

      {draft && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="bg-accent absolute top-1 right-1 size-7"
          aria-label={t("common.reset")}
          onClick={clear}
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  );
}
